import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-102 La Hacienda (GDD §10.3, issue #98): the zoo of the Hacienda, written by hand with the format of
// §15.6. 600 u at 5.25 u/s: takeoff, warm-up (20 %), tension (32 %), CP-102-A at x 300, respite (10 %),
// peak (38 %) and the airstrip. Giraffes are the gap columns, every 9 u with the 4.0 u gap of world 1
// (§5.4). Hazards of the world: gallinazos (EN-01) in the warm-up, hippos (EN-11) jumping from lagoons
// under high gaps, paragliders (EN-04) descending over low gaps, the first armor before the checkpoint,
// the scapular of world 1 on a risk route above the gaps, and Veneno crossing three times on his motor
// paraglider (EN-04 with params.motor, announced with INTERCEPT), never within 5 s of the checkpoint.
// Moving enemies are placed where they meet the plane plus the ground they cover first. All x are local.

const giraffe = (x: number, gapY: number, skin: 'w1-giraffe-a' | 'w1-giraffe-b'): ColumnDef => ({ x, gapY, gapH: 4.0, skin });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const scapular = (x: number, y: number): PickupDef => ({ x, y, type: 'scapular' });
const gallinazo = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-01' });
/** The hippo lives in its lagoon (y 1) and jumps every 2 s once it has been in view. */
const hippo = (x: number): EnemyDef => ({ x, y: 1, type: 'EN-11', params: { repeatEverySeconds: 2 } });
const paraglider = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-04' });
/** Veneno (§4.3): EN-04 with a motor, flying level to the left; a minijefe that withdraws, never defeated. */
const veneno = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-04', params: { motor: 1 } });

const takeoff: SegmentDef = { id: 'w1-hacienda-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w1-hacienda-zoo-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    giraffe(20.0, 6.0, 'w1-giraffe-a'),
    giraffe(29.0, 6.5, 'w1-giraffe-b'),
    giraffe(38.0, 6.0, 'w1-giraffe-a'),
    giraffe(47.0, 5.5, 'w1-giraffe-b'),
  ],
  enemies: [
    gallinazo(36.2, 8.80),
  ],
  pickups: [
    dollar(23.5, 6.25),
    dollar(24.5, 6.55),
    dollar(25.5, 6.25),
  ],
};

const warmup02: SegmentDef = {
  id: 'w1-hacienda-zoo-warmup-02',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    giraffe(6.0, 6.0, 'w1-giraffe-a'),
    giraffe(15.0, 6.5, 'w1-giraffe-b'),
    giraffe(24.0, 6.0, 'w1-giraffe-a'),
    giraffe(33.0, 6.5, 'w1-giraffe-b'),
    giraffe(42.0, 7.0, 'w1-giraffe-a'),
  ],
  enemies: [
    gallinazo(22.2, 3.70),
  ],
  pickups: [
    dollar(9.5, 6.25),
    dollar(10.5, 6.55),
    dollar(11.5, 6.25),
    dollar(36.5, 6.75),
    dollar(37.5, 7.05),
    dollar(38.5, 6.75),
  ],
};

const tension01: SegmentDef = {
  id: 'w1-hacienda-zoo-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    giraffe(1.0, 6.0, 'w1-giraffe-a'),
    giraffe(10.0, 7.5, 'w1-giraffe-b'),
    giraffe(19.0, 6.0, 'w1-giraffe-a'),
    giraffe(28.0, 7.5, 'w1-giraffe-b'),
    giraffe(37.0, 8.0, 'w1-giraffe-a'),
    giraffe(46.0, 6.5, 'w1-giraffe-b'),
  ],
  enemies: [
    hippo(32.5),
  ],
  pickups: [
    dollar(13.5, 6.75),
    dollar(14.5, 7.05),
    dollar(15.5, 6.75),
    dollar(49.5, 6.75),
  ],
};

const tension02: SegmentDef = {
  id: 'w1-hacienda-zoo-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    giraffe(5.0, 7.0, 'w1-giraffe-a'),
    giraffe(14.0, 5.5, 'w1-giraffe-b'),
    giraffe(23.0, 7.0, 'w1-giraffe-a'),
    giraffe(32.0, 5.0, 'w1-giraffe-b'),
    giraffe(41.0, 4.5, 'w1-giraffe-a'),
  ],
  enemies: [
    paraglider(36.5, 11.5),
  ],
  pickups: [
    dollar(0.5, 7.05),
    dollar(1.5, 6.75),
    dollar(17.5, 6.25),
    dollar(18.5, 6.55),
    dollar(19.5, 6.25),
    armor(9.5, 6.25),
  ],
};

const tension03: SegmentDef = {
  id: 'w1-hacienda-zoo-tension-03',
  kind: 'pattern',
  phase: 'tension',
  length: 60,
  columns: [
    giraffe(0.0, 6.5, 'w1-giraffe-a'),
    giraffe(9.0, 5.5, 'w1-giraffe-b'),
    giraffe(18.0, 7.5, 'w1-giraffe-a'),
    giraffe(27.0, 8.0, 'w1-giraffe-b'),
    giraffe(36.0, 6.0, 'w1-giraffe-a'),
    giraffe(45.0, 7.0, 'w1-giraffe-b'),
    giraffe(54.0, 6.0, 'w1-giraffe-a'),
  ],
  enemies: [
    veneno(28.7, 4.0),
  ],
  pickups: [
    dollar(3.5, 6.00),
    dollar(4.5, 6.30),
    dollar(5.5, 6.00),
    dollar(39.5, 6.50),
    dollar(40.5, 6.80),
    dollar(41.5, 6.50),
  ],
};

const respite01: SegmentDef = {
  id: 'w1-hacienda-zoo-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 50,
  columns: [
    giraffe(3.0, 6.5, 'w1-giraffe-a'),
    giraffe(12.0, 6.0, 'w1-giraffe-b'),
    giraffe(21.0, 6.5, 'w1-giraffe-a'),
    giraffe(30.0, 6.0, 'w1-giraffe-b'),
    giraffe(39.0, 6.5, 'w1-giraffe-a'),
    giraffe(48.0, 6.0, 'w1-giraffe-b'),
  ],
  enemies: [],
  pickups: [
    dollar(15.5, 6.25),
    dollar(16.5, 6.55),
    dollar(17.5, 6.25),
    dollar(33.5, 6.25),
    dollar(34.5, 6.55),
    dollar(35.5, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w1-hacienda-zoo-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 65,
  columns: [
    giraffe(7.0, 8.5, 'w1-giraffe-a'),
    giraffe(16.0, 5.5, 'w1-giraffe-b'),
    giraffe(25.0, 8.0, 'w1-giraffe-a'),
    giraffe(34.0, 7.5, 'w1-giraffe-b'),
    giraffe(43.0, 8.0, 'w1-giraffe-a'),
    giraffe(52.0, 5.5, 'w1-giraffe-b'),
    giraffe(61.0, 5.0, 'w1-giraffe-a'),
  ],
  enemies: [
    hippo(38.5),
  ],
  pickups: [
    dollar(10.5, 7.00),
    dollar(11.5, 7.30),
    dollar(12.5, 7.00),
    dollar(55.5, 5.25),
    dollar(56.5, 5.55),
    dollar(57.5, 5.25),
  ],
};

const peak02: SegmentDef = {
  id: 'w1-hacienda-zoo-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 65,
  columns: [
    giraffe(5.0, 4.5, 'w1-giraffe-a'),
    giraffe(14.0, 5.0, 'w1-giraffe-b'),
    giraffe(23.0, 4.0, 'w1-giraffe-a'),
    giraffe(32.0, 7.0, 'w1-giraffe-b'),
    giraffe(41.0, 5.0, 'w1-giraffe-a'),
    giraffe(50.0, 7.5, 'w1-giraffe-b'),
    giraffe(59.0, 8.0, 'w1-giraffe-a'),
  ],
  enemies: [
    hippo(54.5),
    paraglider(18.5, 11.5),
    veneno(6.7, 9.0),
  ],
  pickups: [
    dollar(35.5, 6.00),
    dollar(36.5, 6.30),
    dollar(37.5, 6.00),
  ],
};

const peak03: SegmentDef = {
  id: 'w1-hacienda-zoo-peak-03',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [
    giraffe(3.0, 5.5, 'w1-giraffe-a'),
    giraffe(12.0, 8.5, 'w1-giraffe-b'),
    giraffe(21.0, 7.5, 'w1-giraffe-a'),
    giraffe(30.0, 8.0, 'w1-giraffe-b'),
    giraffe(39.0, 6.0, 'w1-giraffe-a'),
    giraffe(48.0, 6.5, 'w1-giraffe-b'),
  ],
  enemies: [
    veneno(31.7, 4.0),
  ],
  pickups: [
    dollar(15.5, 8.00),
    dollar(16.5, 8.30),
    dollar(17.5, 8.00),
    dollar(33.5, 7.00),
    dollar(34.5, 7.30),
    dollar(35.5, 7.00),
    scapular(7.5, 10.8),
  ],
};

const airstrip: SegmentDef = { id: 'w1-hacienda-airstrip-01', kind: 'airstrip', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_102_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, warmup02, tension01, tension02, tension03, respite01, peak01, peak02, peak03, airstrip];

export const OP_102: LevelDef = {
  id: '1-2',
  op: 'OP-102',
  name: 'La Hacienda',
  world: 1,
  scrollSpeed: 5.25,
  segments: OP_102_SEGMENTS.map((segment) => segment.id),
  checkpoints: [{ id: 'CP-102-A', x: 300 }],
  ending: 'airstrip',
};
